import { formatEuro } from '../../utils/currency';
import type { AgentSummary, InfluencerSummary } from '../../types/financialReport';

interface AgentBreakdownProps {
  agents: AgentSummary[];
  influencers: InfluencerSummary[];
  loading: boolean;
}

export function AgentBreakdown({ agents, influencers, loading }: AgentBreakdownProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1].map(i => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            {[...Array(4)].map((_, j) => (
              <div key={j} className="h-12 bg-gray-50 rounded animate-pulse mb-2" />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const maxAgentFee = Math.max(...agents.map(a => a.total_fees), 1);
  const maxInfluencerFee = Math.max(...influencers.map(i => i.total_fees), 1);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Agent Performance</h3>
          <p className="text-xs text-gray-500 mt-0.5">Total fees and commissions managed per agent</p>
        </div>
        <div className="divide-y divide-gray-50">
          {agents.map((agent) => (
            <div key={agent.name} className="px-6 py-4 hover:bg-gray-50/50 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{agent.name}</p>
                  <p className="text-xs text-gray-500">{agent.campaign_count} campaigns</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">{formatEuro(agent.total_fees)}</p>
                  <p className="text-xs text-teal-600 font-medium">{formatEuro(agent.total_commissions)} commission</p>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500"
                  style={{ width: `${(agent.total_fees / maxAgentFee) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-900">Top Influencers</h3>
          <p className="text-xs text-gray-500 mt-0.5">Highest fee volume across campaigns (top 15)</p>
        </div>
        <div className="divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
          {influencers.slice(0, 15).map((inf, index) => (
            <div key={inf.name} className="px-6 py-3 hover:bg-gray-50/50 transition-colors">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2.5">
                  <span className="text-xs font-bold text-gray-400 w-5 text-right">{index + 1}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{inf.name}</p>
                    <p className="text-xs text-gray-500">{inf.campaign_count} campaigns</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-900">{formatEuro(inf.total_fees)}</p>
                  <div className="flex gap-2 text-xs text-gray-500">
                    <span>Recv: {formatEuro(inf.total_client_invoiced)}</span>
                  </div>
                </div>
              </div>
              <div className="h-1 rounded-full bg-gray-100 overflow-hidden ml-7">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-400 to-emerald-500 transition-all duration-500"
                  style={{ width: `${(inf.total_fees / maxInfluencerFee) * 100}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
